// Rascunho do formulario de recorrencia (painel) <-> regra do shared.
// Datas digitadas sao do horario de Brasilia (-03:00, mesma premissa do shared).

import {
  MAX_INTERVAL_MINUTES,
  MIN_INTERVAL_MINUTES,
  normalizeRecurrenceRule,
  serializeRecurrenceRule,
  type RecurrenceRule,
} from "@lucralink/shared";

export type RecurrenceMode = "weekly" | "interval";

export interface RecurrenceDraft {
  readonly mode: RecurrenceMode;
  readonly days: readonly number[];
  readonly times: readonly string[];
  readonly intervalHours: number;
  /** YYYY-MM-DD (obrigatorio). */
  readonly startDate: string;
  /** HH:MM do inicio (ancora do intervalo). */
  readonly startTime: string;
  /** YYYY-MM-DD ou "" (sem fim). */
  readonly endDate: string;
}

export const WEEKDAY_CHIPS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"] as const;
export const MIN_INTERVAL_HOURS = MIN_INTERVAL_MINUTES / 60;
export const MAX_INTERVAL_HOURS = MAX_INTERVAL_MINUTES / 60;

/** Hoje (YYYY-MM-DD) no fuso de Brasilia. */
export function todayInBrasilia(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(now);
}

export function defaultDraft(now: Date = new Date()): RecurrenceDraft {
  return { mode: "weekly", days: [1, 2, 3, 4, 5], times: ["09:00"], intervalHours: 4, startDate: todayInBrasilia(now), startTime: "00:00", endDate: "" };
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Inicio (data + hora em Brasilia). Data ou hora invalida = null. */
export function draftStart(draft: RecurrenceDraft): Date | null {
  if (!DATE_RE.test(draft.startDate) || !TIME_RE.test(draft.startTime)) return null;
  const date = new Date(`${draft.startDate}T${draft.startTime}:00-03:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Fim do dia (23:59:59 em Brasilia), ou null sem fim. */
export function draftEnd(draft: RecurrenceDraft): Date | null {
  if (!DATE_RE.test(draft.endDate)) return null;
  const date = new Date(`${draft.endDate}T23:59:59-03:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function draftToRule(draft: RecurrenceDraft): RecurrenceRule | null {
  if (draft.mode === "interval") {
    return normalizeRecurrenceRule({ kind: "interval", everyMinutes: Math.round(draft.intervalHours * 60) });
  }
  return normalizeRecurrenceRule({ kind: "weekly", days: [...draft.days], times: draft.times.filter((t) => t.length > 0) });
}

/** Rascunho a partir de uma recorrencia salva (edicao). */
export function draftFromRule(rule: RecurrenceRule, startsAt: string, endsAt: string | null): RecurrenceDraft {
  const toDay = (iso: string): string => todayInBrasilia(new Date(iso));
  const startTime = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(startsAt));
  const base = { startDate: toDay(startsAt), startTime, endDate: endsAt ? toDay(endsAt) : "" };
  if (rule.kind === "interval") {
    return { ...base, mode: "interval", days: [1, 2, 3, 4, 5], times: ["09:00"], intervalHours: rule.everyMinutes / 60 };
  }
  const serialized = serializeRecurrenceRule(rule) as { days: number[]; times: string[] };
  return { ...base, mode: "weekly", days: serialized.days, times: serialized.times, intervalHours: 4 };
}

/** Liga/desliga um dia sem mutar o rascunho. */
export function toggleDay(draft: RecurrenceDraft, day: number): RecurrenceDraft {
  const days = draft.days.includes(day) ? draft.days.filter((d) => d !== day) : [...draft.days, day].sort((a, b) => a - b);
  return { ...draft, days };
}
